import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/** Fire-and-forget insert – errors are silently swallowed so tracking never breaks the app */
const trackEvent = async (
  userId: string,
  type: string,
  value?: number,
  metadata?: Record<string, unknown>,
) => {
  try {
    await (supabase.from('user_activity' as any) as any).insert({
      user_id: userId,
      type,
      ...(value !== undefined ? { value } : {}),
      ...(metadata ? { metadata } : {}),
    });
  } catch {
    // silent – tracking must never break the app
  }
};

/** Track screen open events. No-op if userId is missing. */
export const trackScreenOpen = (userId: string | null | undefined, screen: string) => {
  if (!userId) return;
  void trackEvent(userId, 'screen_open', undefined, { screen });
};

/** Track game open events. No-op if userId is missing. */
export const trackGameOpen = (
  userId: string | null | undefined,
  trainingType: string,
  category?: string,
) => {
  if (!userId) return;
  void trackEvent(userId, 'game_open', undefined, {
    training_type: trainingType,
    ...(category ? { category } : {}),
  });
};

/** Track coach_session_open events when a personal training session actually starts. No-op if userId is missing. */
export const trackCoachSessionOpen = (
  userId: string | null | undefined,
  metadata?: Record<string, unknown>,
) => {
  if (!userId) return;
  void trackEvent(userId, 'coach_session_open', undefined, metadata);
};

/** Track pro_plan_selected (Wechsel monthly/yearly auf /pro). No-op if userId is missing. */
export const trackProPlanSelected = (userId: string | null | undefined, plan: string) => {
  if (!userId) return;
  void trackEvent(userId, 'pro_plan_selected', undefined, { plan });
};

/** Track pro_order_click (Klick auf „Zahlungspflichtig bestellen"). No-op if userId is missing. */
export const trackProOrderClick = (
  userId: string | null | undefined,
  plan: string,
  consentsComplete: boolean,
) => {
  if (!userId) return;
  void trackEvent(userId, 'pro_order_click', undefined, {
    plan,
    consents_complete: consentsComplete,
  });
};

/** Track checkout_start events (user clicked buy → redirect to Stripe). No-op if userId is missing. */
export const trackCheckoutStart = (userId: string | null | undefined, plan?: string) => {
  if (!userId) return;
  void trackEvent(userId, 'checkout_start', undefined, plan ? { plan } : undefined);
};


/** Track checkout_return events after Stripe redirects back. No-op if userId is missing. */
export const trackCheckoutReturn = (
  userId: string | null | undefined,
  result: 'success' | 'cancelled',
) => {
  if (!userId) return;
  void trackEvent(userId, 'checkout_return', undefined, { result });
};

/**
 * Track board_manager_store_click (Klick auf den Microsoft-Store-Downloadlink der Board-Manager-Seite).
 * Zählt nur Klicks, keine Installationen. Fire-and-forget; no-op ohne userId.
 */
export const trackBoardManagerStoreClick = (userId: string | null | undefined) => {
  if (!userId) return;
  void trackEvent(userId, 'board_manager_store_click');
};

/**
 * Tracks app_open once per mount for any authenticated user (covers returning
 * users with a restored session). login is tracked separately via AuthContext's
 * signInTick, which only increments on real SIGNED_IN events (not on the
 * initial session restore after page load).
 */
export const useActivityTracker = () => {
  const { user, signInTick } = useAuth();
  const appOpenTracked = useRef(false);
  const lastLoginTick = useRef(0);

  useEffect(() => {
    if (!user?.id || appOpenTracked.current) return;
    appOpenTracked.current = true;
    trackEvent(user.id, 'app_open');
  }, [user?.id]);

  useEffect(() => {
    if (signInTick > lastLoginTick.current && user?.id) {
      lastLoginTick.current = signInTick;
      trackEvent(user.id, 'login');
    }
  }, [signInTick, user?.id]);
};

/**
 * Fires trackScreenOpen exactly once per mount for the given screen,
 * as soon as a userId is available. Safe against re-renders.
 */
export const useTrackScreenOpen = (userId: string | null | undefined, screen: string) => {
  const fired = useRef(false);
  useEffect(() => {
    if (!userId || fired.current) return;
    fired.current = true;
    trackScreenOpen(userId, screen);
  }, [userId, screen]);
};
